import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import { Placeholder } from "@tiptap/extensions"
import Typography from "@tiptap/extension-typography"
import BubbleMenu from "@tiptap/extension-bubble-menu"

export default class extends Controller {
  static targets = ["input", "menu", "status"]
  static values = {
    saveUrl: String,
    csrfToken: String
  }

  connect() {
    // BubbleMenu v3 starts in its hidden state but never hides the element,
    // so detach it here. The plugin appends it back on a text selection.
    this.menu = this.menuTarget
    this.content = this.element.querySelector("[data-editor-content]")
    this.menu.remove()

    this.editor = new Editor({
      element: this.content,
      extensions: [
        // StarterKit v3 adds Underline, but the notes sanitizer strips <u>.
        StarterKit.configure({ link: { openOnClick: false }, underline: false }),
        Placeholder.configure({ placeholder: "Write your notes..." }),
        Typography,
        BubbleMenu.configure({
          element: this.menu,
          appendTo: () => this.element,
          options: { placement: "top" }
        })
      ],
      content: this.inputTarget.innerHTML || "",
      editorProps: {
        attributes: {
          class: "editor-content focus:outline-none min-h-[120px]"
        }
      },
      onUpdate: () => this.scheduleAutosave()
    })
  }

  disconnect() {
    if (this.saveTimer) clearTimeout(this.saveTimer)
    if (this.editor) {
      // Flush any pending save before teardown
      if (this.dirty) this.save()
      this.editor.destroy()
    }
    // Restore the menu so a Turbo snapshot keeps the original markup
    if (this.menu && !this.menu.isConnected) this.content.before(this.menu)
  }

  // --- Bubble menu actions ---

  toggleBold() { this.editor.chain().focus().toggleBold().run() }
  toggleItalic() { this.editor.chain().focus().toggleItalic().run() }
  toggleCode() { this.editor.chain().focus().toggleCode().run() }

  toggleLink() {
    if (this.editor.isActive("link")) {
      this.editor.chain().focus().unsetLink().run()
      return
    }
    const url = window.prompt("URL")
    if (url) {
      this.editor.chain().focus().setLink({ href: url }).run()
    }
  }

  // --- Autosave ---

  scheduleAutosave() {
    // Keep the template current. A Turbo back navigation restores a page
    // snapshot, and connect() loads the editor from this template.
    this.inputTarget.innerHTML = this.editor.getHTML()
    this.dirty = true
    if (this.saveTimer) clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => this.save(), 1500)
  }

  async save() {
    if (!this.dirty) return
    this.dirty = false
    this.showStatus("Saving...")

    try {
      const response = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        },
        body: JSON.stringify({ item: { notes: this.editor.getHTML() } })
      })

      if (!response.ok) throw new Error(`Save failed (${response.status})`)
      this.showStatus("Saved")
      this.clearStatusAfter(2000)
    } catch (error) {
      this.dirty = true
      this.showStatus("Save failed")
    }
  }

  showStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  clearStatusAfter(ms) {
    setTimeout(() => {
      if (this.hasStatusTarget && this.statusTarget.textContent === "Saved") {
        this.statusTarget.textContent = ""
      }
    }, ms)
  }
}
